import InputAdornment from '@mui/material/InputAdornment'
import TextField, { TextFieldProps } from '@mui/material/TextField'
import { capitalCase, sentenceCase } from 'change-case'
import { SyntheticEvent } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

export interface RHFPercentFieldProps {
  id?: string
  name: string
  onChangeCallback?: (arg0: SyntheticEvent) => void
  forceNegative?: boolean
}

export function RHFPercentField({ id, name, onChangeCallback, label, forceNegative, ...other }: RHFPercentFieldProps & TextFieldProps) {
  const { t } = useTranslation()
  const { control } = useFormContext()

  if (!label) {
    label = sentenceCase(name || '')
  }

  const transform = {
    // This is displayed on the UI
    input: value => {
      if (isNaN(value) || value === 0) {
        // return 0
        return null
      }

      if (forceNegative && value > 0) {
        return Number(Number(value * -100.0).toFixed(2))
      }

      return Number(Number(value * 100.0).toFixed(2))
    }, // This is the stored backend value
    output: e => Number(Number(e.target.value / 100.0).toFixed(4))
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value, ref, ...field }, fieldState: { error } }) => (
        <TextField
          {...field}
          {...other}
          inputRef={ref}
          inputProps={{
            maxLength: 8,
            step: '0.01',
            lang: 'en-US',
            ...other?.inputProps
          }}
          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment>, ...other?.InputProps }}
          id={id ?? name}
          value={transform.input(value)}
          type={'number'}
          label={capitalCase(t((label as string) ?? name ?? '') as string)}
          fullWidth
          error={!!error}
          helperText={error?.message}
          size={other.size ?? 'small'}
          onChange={e => {
            onChange(e)
            onChangeCallback?.(e)
            onChange(transform.output(e))
          }}
        />
      )}
    />
  )
}
